import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type {
  ListAvailabilityInput,
  ListAvailabilityMismatchesInput,
  ListWaitingAvailabilityInput,
} from "@nexxt/contracts/logistics";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the availability the IMS sends (I-12, 50-api/logistics.md §4): what each SKU has in a warehouse,
// with the time of the IMS and the units on hold of an inbound (ADR-145), the rows Nexxt could not resolve, and the
// SKUs a full publication found different from the events (ADR-242). Keys of its own: a movement does not change it,
// the IMS does.

const availability = api.logistics.availability;
const waiting = api.logistics["waiting-availability"];
const mismatches = api.logistics["availability-mismatches"];

export type StockAvailability = InferResponseType<typeof availability.$get, 200>["items"][number];
export type WaitingAvailability = InferResponseType<typeof waiting.$get, 200>["items"][number];
export type AvailabilityMismatch = InferResponseType<typeof mismatches.$get, 200>["items"][number];

/** The filters of the list input (`listAvailabilityInput`), without the paging the queries add. */
export type StockAvailabilityFilters = Pick<ListAvailabilityInput, "location" | "q" | "sort">;

/** The filters of the rows that wait (`listWaitingAvailabilityInput`). */
export type WaitingAvailabilityFilters = Pick<ListWaitingAvailabilityInput, "reason">;

/** The filters of the mismatches (`listAvailabilityMismatchesInput`): open when the URL says no state. */
export type AvailabilityMismatchFilters = Pick<ListAvailabilityMismatchesInput, "location" | "state">;

/** The SKUs a search shows: a code typed names a few. */
const FOUND = 20;
const PAGE_SIZE = 50;

const all = ["stock-availability"] as const;

export const stockAvailabilityQueries = {
  all,

  list: (filters: StockAvailabilityFilters) =>
    infiniteQueryOptions({
      queryKey: [...all, "list", filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          availability.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The first SKUs of a warehouse whose code or EAN holds what is typed: a picker of what can leave it. */
  search: (filters: Pick<StockAvailabilityFilters, "location" | "q">) =>
    queryOptions({
      queryKey: [...all, "search", filters],
      queryFn: ({ signal }) =>
        unwrap(
          availability.$get({ query: { ...filterQuery(filters), pageSize: String(FOUND) } }, { init: { signal } }),
        ),
    }),

  /** The SKUs a full publication found different from the events (ADR-242), the latest first. */
  mismatches: (filters: AvailabilityMismatchFilters) =>
    infiniteQueryOptions({
      queryKey: [...all, "mismatches", filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          mismatches.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The rows of the IMS Nexxt could not resolve (I-12 §5), the latest first. */
  waiting: (filters: WaitingAvailabilityFilters) =>
    infiniteQueryOptions({
      queryKey: [...all, "waiting", filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          waiting.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),
};
